/**
 * EdgeStream - Main Facade
 *
 * Top-level API for orchestrating all components.
 */

import type {
  IEdgeStream,
  EdgeStreamConfig,
  EdgeStreamStatus,
  IServer,
  ISubscription,
  SubscriberCallback,
  EdgeStreamEventType,
  EdgeStreamEventHandler,
  IEnvelope,
} from './types';
import { Server } from './servers/Server';
import { createTransport } from './transports/TransportFactory';
import type { IServerRegistration } from './types';
import { createModuleLogger, setLogLevel } from './utils';

export class EdgeStream implements IEdgeStream {
  readonly servers: Map<string, IServer> = new Map();

  status: EdgeStreamStatus = 'idle';

  private logger = createModuleLogger('EdgeStream');

  private eventHandlers: Map<EdgeStreamEventType, Set<EdgeStreamEventHandler>> = new Map();

  constructor(config: EdgeStreamConfig = {}) {
    if (config.logLevel) {
      setLogLevel(config.logLevel);
    }
    this.logger.info(`EdgeStream initialized with logLevel ${config.logLevel || 'info'}`);
  }

  async start(): Promise<void> {
    if (this.status === 'running') {
      return;
    }

    this.status = 'starting';

    try {
      const startPromises = Array.from(this.servers.values()).map((server) => server.start());
      await Promise.all(startPromises);

      this.status = 'running';
      this.emit('stream:started', { timestamp: new Date() });
      this.logger.info('EdgeStream started');
    } catch (error) {
      this.status = 'error';
      this.emit('stream:error', { timestamp: new Date(), data: { error: String(error) } });
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (this.status === 'idle') {
      return;
    }

    this.status = 'stopping';

    try {
      const stopPromises = Array.from(this.servers.values()).map((server) => server.stop());
      await Promise.all(stopPromises);

      this.status = 'idle';
      this.emit('stream:stopped', { timestamp: new Date() });
      this.logger.info('EdgeStream stopped');
    } catch (error) {
      this.status = 'error';
      this.emit('stream:error', { timestamp: new Date(), data: { error: String(error) } });
      throw error;
    }
  }

  registerServer(registration: IServerRegistration): void {
    if (this.servers.has(registration.id)) {
      this.logger.warn(`Server ${registration.id} already registered`);
      return;
    }

    const transport = createTransport(registration.transportConfig);
    const server = new Server(registration, transport);
    this.servers.set(registration.id, server);

    this.logger.info(`Server ${registration.id} registered (${registration.type})`);
  }

  server(serverId: string): IServer | undefined {
    return this.servers.get(serverId);
  }

  subscribe<TBody = unknown>(
    serverId: string,
    topic: string,
    callback: SubscriberCallback<TBody>,
  ): ISubscription {
    const server = this.servers.get(serverId);
    if (!server) {
      throw new Error(`Server ${serverId} not found`);
    }
    return server.subscriptionManager.subscribe(topic, callback);
  }

  async send(serverId: string, topic: string, body: unknown): Promise<void> {
    const server = this.servers.get(serverId);
    if (!server) {
      throw new Error(`Server ${serverId} not found`);
    }
    await server.send(topic, body);
  }

  on(event: EdgeStreamEventType, handler: EdgeStreamEventHandler): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    const handlers = this.eventHandlers.get(event)!;
    handlers.add(handler);

    return () => {
      handlers.delete(handler);
    };
  }

  getMessageCache(_serverId: string): IEnvelope[] | undefined {
    // Message cache will be implemented in Phase 9 (Zustand stores)
    return undefined;
  }

  private emit(event: EdgeStreamEventType, eventData: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(eventData);
        } catch (error) {
          this.logger.error(`Event handler error for ${event}`, error as Error);
        }
      }
    }
  }
}

/**
 * Factory function to create EdgeStream instance
 */
export function createEdgeStream(config: EdgeStreamConfig = {}): EdgeStream {
  return new EdgeStream(config);
}
